import React, { useState } from 'react';
import { Logo } from '../../components/branding/Logo';
import { userDataService } from '../../services/UserDataService';
import { useAuth } from '../AuthContext';
import './AccessGate.css';

export const AccessGate: React.FC = () => {
  const { user, accessStatus, signOut, refreshAccessStatus } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const handleRequest = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await userDataService.submitAccessRequest(user);
      await refreshAccessStatus();
    } catch (e) {
      console.error('Failed to submit access request:', e);
      setError('Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error('Sign out failed:', e);
    }
  };

  return (
    <div className="access-gate">
      <div className="access-gate-card">
        <Logo variant="stacked" size="medium" />

        <div className="access-gate-user">
          {user.photoURL && <img src={user.photoURL} alt="" className="access-gate-avatar" />}
          <div className="access-gate-user-meta">
            {user.displayName && <div className="access-gate-name">{user.displayName}</div>}
            <div className="access-gate-email">{user.email}</div>
          </div>
        </div>

        {accessStatus === 'no_request' && (
          <>
            <h1 className="access-gate-title">Access required</h1>
            <p className="access-gate-message">
              You're signed in, but this account doesn't have access to Code League yet.
              Submit a request and an administrator will review it.
            </p>
            {error && <div className="access-gate-error">{error}</div>}
            <button
              className="access-gate-primary"
              onClick={handleRequest}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Request access'}
            </button>
          </>
        )}

        {accessStatus === 'pending' && (
          <>
            <h1 className="access-gate-title">Request pending</h1>
            <p className="access-gate-message">
              Your access request is waiting for approval. You'll be able to play
              once an administrator approves it. Refresh this page to check again.
            </p>
            <button className="access-gate-secondary" onClick={() => refreshAccessStatus()}>
              Check again
            </button>
          </>
        )}

        {accessStatus === 'denied' && (
          <>
            <h1 className="access-gate-title">Request not approved</h1>
            <p className="access-gate-message">
              Your access request was not approved. If you think this is a mistake,
              contact the administrator.
            </p>
          </>
        )}

        <button className="access-gate-link" onClick={handleSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );
};
